import { toastAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { ICONS_MAP, NOTIFICATION_VARIANTS, ROOT_MAP, TEXT_MAP } from '../common/AlertAndToast.common';
import { rem } from '../themeUtils';

const toastSlotRecipe = defineSlotRecipe({
  className: 'toast',
  slots: [...toastAnatomy.keys(), 'content', 'icon', 'timestamp'],
  base: {
    actionTrigger: {
      marginBlockStart: '16',
    },
    content: {
      flex: 1,
      minWidth: 0,
      paddingInlineEnd: '16',
    },
    description: {
      paddingBlockStart: '2',
      textStyle: 'comp/notification/message',
    },
    icon: {
      alignSelf: 'flex-start',
      flexShrink: 0,
      height: '24',
      marginInlineEnd: '8',
      width: '24',
    },
    root: {
      alignItems: 'flex-start',
      border: '1px solid',
      borderRadius: '8',
      boxShadow: 'elevation/lg',
      display: 'flex',
      // `rem(15)`/`rem(11)` padding (not 16/12) lands the toast on the designed geometry once the
      // 1px border is added — Figma measures its padding from the frame edge, stroke included:
      // heights 56px message-only, 102px message + action, 116px title + message + timestamp,
      // 128px title + message + action, and 12px from the inline edges on both sides. Same
      // compensation as BitkitAlert's `rem(11)`.
      paddingBlock: rem(15),
      paddingInline: rem(11),
      position: 'relative',
      translate: 'var(--x) var(--y)',
      width: rem(480),
      willChange: 'translate, opacity, scale',
      transition: 'translate 400ms, scale 400ms, opacity 400ms, height 400ms, box-shadow 200ms',
      transitionTimingFunction: 'cubic-bezier(0.21, 1.02, 0.73, 1)',
      _closed: {
        transition: 'translate 400ms, scale 400ms, opacity 200ms',
        transitionTimingFunction: 'cubic-bezier(0.06, 0.71, 0.55, 1)',
      },
    },
    timestamp: {
      marginBlockStart: '16',
      textStyle: 'comp/notification/message',
    },
    title: {
      marginBlockEnd: '2',
      textStyle: 'comp/notification/title',
    },
    closeTrigger: {
      // Out of flow, as Figma models it: `closeButton-margin` spans the full toast height in
      // every variant with the button pinned at its top, so the toast's height comes from the
      // content alone. `rem(11)` for the same border compensation as the root padding — 12px
      // from the frame edge, which puts the 32px button at x436 y12 of the 480px frame.
      insetBlockStart: rem(11),
      insetInlineEnd: rem(11),
      position: 'absolute',
    },
  },
  variants: {
    // The out-of-flow close button reserves its own space: 48px = its 32px box plus the 16px
    // gutter Figma's `content-block` keeps after the text column. Without one the gutter is
    // the base 16px, which is what widens the text column from 376px to 408px.
    closable: {
      true: {
        content: {
          paddingInlineEnd: '48',
        },
      },
      false: {},
    },
    variant: Object.fromEntries(
      NOTIFICATION_VARIANTS.map((v) => [
        v,
        {
          icon: { color: ICONS_MAP[v] },
          root: { ...ROOT_MAP[v], color: TEXT_MAP[v] },
        },
      ]),
    ),
  },
  defaultVariants: {
    variant: 'info',
  },
});

export default toastSlotRecipe;
